"use client";
/* eslint-disable react-hooks/set-state-in-effect */

import { useEffect, useState } from "react";
import Link from "next/link";
import { Check, Crown, Sparkles } from "lucide-react";
import BrandLogo from "@/components/tenant/BrandLogo";
import { useTenant, useTerm } from "@/components/tenant/TenantProvider";
import { authApi, membershipApi, userApi } from "@/lib/api-client";
import { formatRupiah } from "@/lib/utils";

interface Plan {
  id: string;
  slug: string;
  name: string;
  description?: string;
  duration_type: "monthly" | "yearly" | "lifetime";
  price: number;
  compare_at_price?: number | null;
  /** Benefit kustom per-plan (diatur admin di /admin/memberships). Kosong = fallback generik. */
  benefits?: string[];
}

interface ActiveMembership {
  plan_name: string;
  plan_slug: string;
  duration_type: "monthly" | "yearly" | "lifetime";
  expires_at: string | null;
}

// Fallback GENERIK (netral, bukan spesifik brand) — dipakai hanya bila admin
// belum mengatur benefit untuk plan tsb di /admin/memberships.
const DEFAULT_YEARLY_FEATURES = [
  "Akses semua kelas premium",
  "Kelas baru otomatis terbuka",
  "Materi & bonus tambahan",
  "Komunitas member",
];

const DEFAULT_LIFETIME_FEATURES = [
  "Semua benefit paket tahunan",
  "Akses selamanya — bayar sekali",
  "Semua kelas baru otomatis terbuka",
  "Tanpa biaya perpanjangan",
];

/** Benefit efektif sebuah plan: kustom admin bila ada, else fallback generik per durasi. */
function planFeatures(plan: Plan): string[] {
  if (plan.benefits && plan.benefits.length > 0) return plan.benefits;
  if (plan.duration_type === "lifetime") return DEFAULT_LIFETIME_FEATURES;
  return DEFAULT_YEARLY_FEATURES;
}

export default function PremiumPageClient() {
  const { brand_name } = useTenant();
  const term = useTerm();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [yearly, setYearly] = useState<Plan | null>(null);
  const [lifetime, setLifetime] = useState<Plan | null>(null);
  const [membership, setMembership] = useState<ActiveMembership | null>(null);
  const [loading, setLoading] = useState(true);
  const [refCode, setRefCode] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref") || "";
    if (ref && /^[A-Za-z0-9_-]{3,60}$/.test(ref)) setRefCode(ref);

    Promise.all([
      membershipApi.plans(),
      // Cek login dulu — /api/user/membership butuh auth, memanggilnya tanpa
      // gate mencatat 401 di console untuk SETIAP pengunjung anonim (Lighthouse
      // Best Practices "no browser errors"), padahal hasilnya toh selalu null.
      authApi.me().then((me) => (me.success ? userApi.membership() : { success: false, data: null })),
    ]).then(([plansRes, membershipRes]) => {
      if (plansRes.success && Array.isArray(plansRes.data)) {
        const list = plansRes.data as Plan[];
        setPlans(list);
        setYearly(list.find((p) => p.slug === "yearly") || list.find((p) => p.duration_type === "yearly") || null);
        setLifetime(list.find((p) => p.slug === "lifetime") || list.find((p) => p.duration_type === "lifetime") || null);
      }
      const m = (membershipRes as { success: boolean; data?: unknown }).data as ActiveMembership | null | undefined;
      if (m && m.plan_slug) setMembership(m);
    }).finally(() => setLoading(false));
  }, []);

  // Halaman /order menandai slug sebagai membership hanya bila berprefiks
  // "membership-" (atau slug literal monthly/yearly/lifetime). Untuk slug plan
  // kustom (mis. "ok"), tanpa prefiks link jatuh ke pencarian kelas/produk →
  // 404. Selalu prefiks "membership-" agar resolusi order benar.
  const orderHref = (slug: string) => {
    const s = slug.startsWith("membership-") ? slug : `membership-${slug}`;
    return `/order/${s}${refCode ? `?ref=${encodeURIComponent(refCode)}` : ""}`;
  };

  const isLifetimeMember = membership?.duration_type === "lifetime";
  const isYearlyMember = membership?.duration_type === "yearly";

  // Hanya render paket NYATA milik tenant — jangan pernah menampilkan harga
  // fallback hardcoded (dulu memunculkan harga GWA & tautan produk yang tak ada).
  const renderCard = (plan: Plan, opts: {
    badge?: string;
    features: string[];
    priceNote: string;
    cta: string;
    featured: boolean;
    alreadyActive?: boolean;
  }) => {
    const name = plan.name;
    const price = plan.price;
    const compareAt = plan.compare_at_price ?? null;
    const slug = plan.slug;
    const hasDiscount = compareAt && compareAt > price;

    return (
      <div
        className={`relative flex flex-col bg-white rounded-2xl border shadow-sm overflow-hidden ${
          opts.featured ? "border-primary ring-1 ring-primary/30" : "border-[rgba(12,12,10,0.08)]"
        }`}
      >
        {opts.badge && (
          <div className="absolute right-4 top-4 rounded-md bg-primary px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[var(--brand-on-primary,#fff)]">
            {opts.badge}
          </div>
        )}
        <div className={`h-1 w-full ${opts.featured ? "bg-primary" : "bg-[rgba(12,12,10,0.08)]"}`} />
        <div className="flex flex-col flex-1 p-6">
          <p className="text-sm font-bold text-warm-black">{name}</p>

          <div className="mt-3">
            {hasDiscount ? (
              <p className="text-sm line-through" style={{ color: "rgba(12,12,10,0.65)" }}>
                {formatRupiah(compareAt!)}
              </p>
            ) : null}
            <p className="text-3xl font-bold text-warm-black">{formatRupiah(price)}</p>
            <p className="text-xs mt-1" style={{ color: "rgba(12,12,10,0.65)" }}>{opts.priceNote}</p>
          </div>

          <ul className="mt-5 space-y-2.5 flex-1">
            {opts.features.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm" style={{ color: "rgba(12,12,10,0.7)" }}>
                <Check className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
                {f}
              </li>
            ))}
          </ul>

          {opts.alreadyActive ? (
            <div className="mt-6 w-full py-2.5 text-center text-sm font-semibold rounded-xl bg-green-50 border border-green-200 text-green-700">
              ✓ Sudah aktif
            </div>
          ) : (
            <Link
              href={orderHref(slug)}
              className={`mt-6 block w-full py-2.5 text-center text-sm font-semibold rounded-xl transition-colors ${
                opts.featured
                  ? "bg-primary text-[var(--brand-on-primary,#fff)] hover:bg-primary-hover"
                  : "border border-primary/40 text-primary hover:bg-[var(--brand-primary-soft)]"
              }`}
            >
              {opts.cta}
            </Link>
          )}
        </div>
      </div>
    );
  };

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="min-h-screen bg-warm-cream"
      style={{
        backgroundImage: `linear-gradient(to right, rgba(12,12,10,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(12,12,10,0.04) 1px, transparent 1px)`,
        backgroundSize: "40px 40px",
      }}
    >
      <div className="mx-auto w-full max-w-3xl px-4 py-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <Link href="/dashboard">
              <BrandLogo width={128} height={32} priority className="h-8 w-auto" />
            </Link>
          </div>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 mb-4">
            <Crown className="h-3.5 w-3.5 text-amber-600" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700">{term("member", "Member")} {term("premium", "Premium")}</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-warm-black tracking-tight">
            Buka Semua Akses {brand_name}
          </h1>
          <p className="mt-2 text-sm leading-relaxed max-w-md mx-auto" style={{ color: "rgba(12,12,10,0.65)" }}>
            Semua kelas, live session, rekaman, bonus & template dalam satu membership. Pilih yang paling cocok untukmu.
          </p>
        </div>

        {loading ? (
          <div className="grid gap-4 md:grid-cols-2">
            {[1, 2].map((i) => (
              <div key={i} className="rounded-2xl border border-[rgba(12,12,10,0.08)] bg-white p-6 shadow-sm">
                <div className="h-5 w-24 skeleton rounded" />
                <div className="mt-4 h-9 w-32 skeleton rounded" />
                <div className="mt-6 space-y-2">
                  {[1, 2, 3, 4].map((j) => (
                    <div key={j} className="h-4 w-full skeleton rounded" />
                  ))}
                </div>
                <div className="mt-6 h-11 w-full skeleton rounded-xl" />
              </div>
            ))}
          </div>
        ) : isLifetimeMember ? (
          /* Sudah Lifetime — tidak ada yang perlu dibeli lagi */
          <div className="bg-white rounded-2xl border border-green-200 shadow-sm p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-50 border border-green-200">
              <Sparkles className="h-7 w-7 text-green-600" />
            </div>
            <p className="text-lg font-bold text-warm-black">Kamu sudah Lifetime Member 🎉</p>
            <p className="mt-1.5 text-sm" style={{ color: "rgba(12,12,10,0.65)" }}>
              Semua akses premium sudah terbuka selamanya di akun kamu.
            </p>
            <Link
              href="/dashboard"
              className="mt-6 inline-block py-2.5 px-6 bg-primary text-[var(--brand-on-primary,#fff)] text-sm font-semibold rounded-xl hover:bg-primary-hover transition-colors"
            >
              Buka Dashboard →
            </Link>
          </div>
        ) : (
          <>
            {isYearlyMember && (
              <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-center">
                <p className="text-xs text-amber-800">
                  Membership <strong>{membership?.plan_name}</strong> kamu aktif
                  {membership?.expires_at
                    ? ` hingga ${new Date(membership.expires_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}`
                    : ""}
                  . Upgrade ke Lifetime supaya tidak perlu perpanjang lagi.
                </p>
              </div>
            )}

            {plans.length === 0 ? (
              /* Tenant belum punya paket membership aktif — jangan tampilkan harga palsu */
              <div className="bg-white rounded-2xl border border-[rgba(12,12,10,0.08)] shadow-sm p-8 text-center">
                <p className="text-base font-bold text-warm-black">Belum ada paket membership</p>
                <p className="mt-1.5 text-sm" style={{ color: "rgba(12,12,10,0.65)" }}>
                  {brand_name} belum membuka paket membership. Sementara itu, jelajahi kelas & produk yang tersedia.
                </p>
                <Link
                  href="/dashboard"
                  className="mt-6 inline-block py-2.5 px-6 bg-primary text-[var(--brand-on-primary,#fff)] text-sm font-semibold rounded-xl hover:bg-primary-hover transition-colors"
                >
                  Buka Dashboard →
                </Link>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 items-stretch">
                {yearly && renderCard(yearly, {
                  badge: isYearlyMember ? undefined : "Paling Populer",
                  features: planFeatures(yearly),
                  priceNote: "per tahun",
                  cta: "Pilih Tahunan →",
                  featured: !isYearlyMember,
                  alreadyActive: isYearlyMember,
                })}
                {lifetime && renderCard(lifetime, {
                  badge: "Sekali Bayar",
                  features: planFeatures(lifetime),
                  priceNote: "sekali bayar · akses selamanya",
                  cta: "Pilih Lifetime →",
                  featured: isYearlyMember,
                })}
                {/* Paket lain milik tenant (mis. bulanan) yang bukan yearly/lifetime */}
                {plans
                  .filter((p) => p.id !== yearly?.id && p.id !== lifetime?.id)
                  .map((p) =>
                    renderCard(p, {
                      features: planFeatures(p),
                      priceNote:
                        p.duration_type === "monthly"
                          ? "per bulan"
                          : p.duration_type === "yearly"
                            ? "per tahun"
                            : "sekali bayar · akses selamanya",
                      cta: `Pilih ${p.name} →`,
                      featured: false,
                    }),
                  )}
              </div>
            )}

            <p className="mt-6 text-center text-xs" style={{ color: "rgba(12,12,10,0.65)" }}>
              Pembayaran aman via QRIS & Virtual Account — semua proses di dalam {brand_name}, akses aktif otomatis setelah bayar.
            </p>
          </>
        )}

        <p className="mt-6 text-center text-sm" style={{ color: "rgba(12,12,10,0.65)" }}>
          <Link href="/dashboard" className="hover:text-primary transition-colors">← Kembali ke Dashboard</Link>
        </p>
      </div>
    </main>
  );
}
